/**
 * calb010 „Podľa firmy“ – stránkovacia jednotka je firma (Company type=calibration),
 * nested certificates mapujú na company_certificates. Endpoint zostáva /api/companies/paged
 * (nie paged_by_company; R3C to používa pre OTK).
 */
import {
  CALIBRATION_COMPANY_CERT_KIND,
  SESSION_INVALIDATING,
  SESSION_SAFE_UPDATE,
} from './calibrationCertificateStateSync';

export { SESSION_INVALIDATING, SESSION_SAFE_UPDATE };

export const CALIBRATION_BY_COMPANY_PAGED_URL = '/api/companies/paged';
export const CALIBRATION_BY_COMPANY_PRINT_URL = '/api/companies/export/print';
export const CALIBRATION_BY_COMPANY_XLSX_URL = '/api/companies/export/xlsx';

/** Zhodné s CompanyListQueryContract::definition() sorters. */
export const CALIBRATION_BY_COMPANY_SORTABLE_FIELDS = Object.freeze([
  'id',
  'name',
  'defined_id',
  'types',
  'address',
  'tel_number',
  'fax',
  'email',
  'web_address',
  'contact_person',
  'description',
  'country',
  'validity',
  'company_status_display',
  'created_at',
  'updated_at',
]);

export function isCalibrationByCompanySortableField(field) {
  return CALIBRATION_BY_COMPANY_SORTABLE_FIELDS.includes(field);
}

export function cleanCalibrationByCompanySorters(sorters) {
  return (Array.isArray(sorters) ? sorters : [])
    .map((sorter) => ({
      field: sorter?.field ?? sorter?.column,
      dir: sorter?.dir === 'desc' ? 'desc' : 'asc',
    }))
    .filter((sorter) => isCalibrationByCompanySortableField(sorter.field));
}

export function calibrationByCompanyShowUrl(companyId, {
  showExcludedCompanies = '0',
  showDeactivatedCerts = '0',
} = {}) {
  const excluded = String(showExcludedCompanies) === '1' ? '1' : '0';
  const deactivated = String(showDeactivatedCerts) === '1' ? '1' : '0';
  return `/api/companies/${companyId}?type=calibration&show_excluded_companies=${excluded}&show_deactivated_certs=${deactivated}`;
}

export function applyCalibrationByCompanyHeaderFiltersToParams(extra, headerFilters) {
  (Array.isArray(headerFilters) ? headerFilters : []).forEach((filter) => {
    const field = filter?.field;
    if (!field) return;
    const value = filter.value;
    if (value == null || String(value).trim() === '') return;
    extra[`filter_${field}`] = String(value).trim();
  });
  return extra;
}

/** Nested payload from companies/paged → flat company_certificates on the row (not dataTree). */
export function decorateCalibrationByCompanyPagedRows(rawRows) {
  return (Array.isArray(rawRows) ? rawRows : []).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const nested = row.certificates || row.company_certificates || [];
    return {
      ...row,
      company_certificates: Array.isArray(nested) ? nested : [],
    };
  });
}

/** Vznik/zánik certifikátu alebo (de)aktivácia mení eligibility firmy → session sa musí reštartovať. */
const MEMBERSHIP_KINDS = new Set([
  CALIBRATION_COMPANY_CERT_KIND.PARENT_CREATED,
  CALIBRATION_COMPANY_CERT_KIND.PARENT_DELETED,
  CALIBRATION_COMPANY_CERT_KIND.PARENT_ACTIVATED,
  CALIBRATION_COMPANY_CERT_KIND.PARENT_DEACTIVATED,
  CALIBRATION_COMPANY_CERT_KIND.RECORD_CREATED,
  CALIBRATION_COMPANY_CERT_KIND.RECORD_DELETED,
]);

/**
 * Riadok zoznamu je firma, preto zmena vnoreného certifikátu/záznamu je bezpečná len vtedy,
 * keď writer poslal `companyId` a daná firma je načítaná v aktuálnej session.
 */
export function classifyCalibrationByCompanyCertMutation(event, session = {}) {
  const detail = event?.detail;
  if (!detail || typeof detail !== 'object') return SESSION_INVALIDATING;
  const kind = detail.kind;
  if (!kind || MEMBERSHIP_KINDS.has(kind)) return SESSION_INVALIDATING;
  const companyId = detail.companyId ?? detail.company_id;
  if (companyId == null || companyId === '') return SESSION_INVALIDATING;
  if (typeof session.isRowLoaded !== 'function' || !session.isRowLoaded(companyId)) return SESSION_INVALIDATING;
  if (String(session.toolbarSearch ?? '').trim() !== '') return SESSION_INVALIDATING;
  return SESSION_SAFE_UPDATE;
}

export function calibrationByCompanyMutationCompanyId(detail) {
  const companyId = detail?.companyId ?? detail?.company_id;
  return companyId == null || companyId === '' ? null : companyId;
}
